import React, { useMemo } from "react";

import type { ProcessedBetEntry } from "@/hooks/firestore/betting/useEntries";

import pluralize from "pluralize";
import { Selection } from "./BaseSelection";
import {
  DiamondIcon as QuaddieIcon,
  QuaddiesIcon as QuaddiesLogo,
} from "@picklebet/ui";
import { useTitle } from "@/hooks/graphql/useTitles";
import { isEntryDecided } from "@/features/entries/util/entry-helpers";
import { isQuaddieSelection } from "@/features/entries/util/type-guards";
import { formatUserDateTime } from "@/utilities/datetime";
import { SELECTION_DATETIME } from "@/utilities/dateTimeFormats";
import { ComplexBetPill } from "./ComplexBetPill";
import { DrawerLineItem } from "../common/DrawerLineItem";
import { QuaddieOutcome } from "../outcomes/QuaddieOutcome";

import * as styles from "./QuaddieSelection.module.scss";

interface QuaddieSelectionProps {
  entry: ProcessedBetEntry;
  isPreview?: boolean;
}

const QuaddieSelection = ({
  entry,
  isPreview = false,
}: QuaddieSelectionProps) => {
  const firstSelection = entry.data.selections[0];
  const isShared = !!entry.shareId;
  const isDecided = isEntryDecided(entry);
  const isEntryWon = entry.data.result === "WIN";
  const combos = entry.data.totalCombinations;

  const title = useTitle(
    isQuaddieSelection(firstSelection) ? firstSelection.event.sport : "",
  );
  const sortedSelections = useMemo(() => {
    return Object.values(entry.data.selections)
      .filter(isQuaddieSelection)
      .sort(
        (a, b) =>
          Number(a.event.attributes?.raceNumber) -
          Number(b.event.attributes?.raceNumber),
      );
  }, [entry]);

  if (!isQuaddieSelection(firstSelection)) {
    return null;
  }

  const venueName = firstSelection.event.attributes?.venueName;

  return (
    <Selection
      isQuaddie
      isSelectionWon={isEntryWon}
      isEntryWon={isEntryWon}
      isDecided={isDecided}
      slots={{
        icon: <QuaddieIcon className={styles.diamondIcon} />,
        outcomeName: venueName,
        marketName: title.name,
        drawerContent: (
          <>
            <DrawerLineItem
              label="Start"
              value={formatUserDateTime(
                firstSelection.event.scheduledStartTime!,
                SELECTION_DATETIME,
              )}
            />

            <DrawerLineItem label="Venue" value={venueName} />

            <DrawerLineItem
              label="Placed"
              value={formatUserDateTime(entry.createdAt, SELECTION_DATETIME)}
            />

            {!isShared && (
              <DrawerLineItem label="Entry Id" value={`B-${entry.entryId}`} />
            )}
          </>
        ),
      }}
    >
      <ComplexBetPill
        icon={<QuaddiesLogo />}
        odds={pluralize("Combo", combos ?? undefined, true)}
        isWon={isEntryWon}
      />

      {sortedSelections.map((selection, index) => (
        <QuaddieOutcome
          key={`quaddie-selection-${index}-${entry.entryId}`}
          entryId={entry.entryId}
          selection={selection}
          isPreview={isPreview}
          isEntryWon={isEntryWon}
        />
      ))}
    </Selection>
  );
};

export { QuaddieSelection };
